import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Grid,
	Typography,
	TextField,
	MenuItem,
	FormControl
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CustomButton from "../../../src/components/CustomButton/CustomButton";
import Layout from "../../components/Wrappers/Layout";
import { Card, Alert } from "reactstrap";
import { useSelector, useDispatch, useStore } from "react-redux";

import {
	getTransactionTypeListDetails
} from "../../redux/actions/getTransactionTypeListAction";

import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import "../../components/LoadingSpinner/spinner.css";
import Util from "../../validation";
import SOLogout from "../../components/Logout/SOLogout";
import regenerateToken from "../../components/RegenerateToken/regenerateToken";
import { getCustomerIdAndNameList } from "../../redux/actions/getCustomerIdAndNameListAction";
import { getTransactionCurrencyListAction } from "../../redux/actions/getTransactionCurrencyList";
import Autocomplete from "@mui/lab/Autocomplete";
import { partnerTransactionSubTypesListReq } from "../../redux/actions/transactionSubTypesPartnerAction";
import { getWalletTransactionList } from "../../redux/actions/getWalletTransactionListAction";






// html2pdf.js is loaded on demand from a CDN at runtime (see loadHtml2Pdf
// below) instead of being an npm dependency — this avoids fighting the
// project's existing peer-dependency conflicts on `npm install`.
// html2pdf/html2canvas is deliberately NOT used any more. html2canvas works
// by rasterizing the whole rendered DOM into a bitmap (at scale:2, i.e. 4x
// the pixels) — for a report with hundreds/thousands of table rows that
// screenshot gets enormous and takes the better part of a minute to produce.
// jsPDF + jspdf-autotable instead draws the table as real vector text
// directly from the `dashboardData` object (no DOM screenshot involved),
// which is dramatically faster and also yields a smaller, sharper,
// text-selectable/searchable PDF.
const JSPDF_CDN_URL = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
const JSPDF_AUTOTABLE_CDN_URL = "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js";

function loadJsPdf() {
	if (window.jspdf && window.jspdf.jsPDF) return Promise.resolve(window.jspdf.jsPDF);
	if (window.__jspdfLoadingPromise) return window.__jspdfLoadingPromise;

	window.__jspdfLoadingPromise = new Promise((resolve, reject) => {
		const coreScript = document.createElement("script");
		coreScript.src = JSPDF_CDN_URL;
		coreScript.async = true;
		coreScript.onload = () => {
			const autoTableScript = document.createElement("script");
			autoTableScript.src = JSPDF_AUTOTABLE_CDN_URL;
			autoTableScript.async = true;
			autoTableScript.onload = () => resolve(window.jspdf.jsPDF);
			autoTableScript.onerror = () => reject(new Error("Could not load the PDF table library."));
			document.body.appendChild(autoTableScript);
		};
		coreScript.onerror = () => reject(new Error("Could not load the PDF export library."));
		document.body.appendChild(coreScript);
	});

	return window.__jspdfLoadingPromise;
}

// =====================================================================
// Pure helpers — mirror the standalone dashboard generator, but read
// straight off the getWalletTransactionList JSON field names.
// =====================================================================
function methodLabel(row) {
	const pm = row.paymentMode;
	const brand = row.paymentBrand;
	if (pm === null || pm === undefined || pm === "") return "Unknown / Not specified";
	const p = String(pm).trim();
	if (p === "CC") return "Credit Card";
	if (p === "DC") return "Debit Card";
	if (p === "BT") return "Open Banking / Pay by Bank";
	if (p === "OBT") return "Offline / Manual Bank Transfer";
	if (p === "Wallet") return `Digital Wallet (${brand || "Other"})`;
	return p;
}

function isDomestic(country) {
	const c = String(country || "").trim().toUpperCase();
	return c === "GBR" || c === "GB";
}

function toDate(v) {
	if (v instanceof Date) return v;
	if (typeof v === "number") {
		return new Date(Math.round((v - 25569) * 86400 * 1000));
	}
	if (typeof v === "string") {
		const cleaned = v.replace(/\t/g, "").trim();
		const d = new Date(cleaned.replace(" ", "T"));
		if (!isNaN(d.getTime())) return d;
		const d2 = new Date(cleaned);
		if (!isNaN(d2.getTime())) return d2;
	}
	return null;
}

function fmtDate(v) {
	const d = toDate(v);
	if (!d) return String(v || "");
	return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(v) {
	const d = toDate(v);
	if (!d) return String(v || "");
	const datePart = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
	const timePart = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
	return `${datePart} ${timePart}`;
}

function num(v) {
	const n = parseFloat(v);
	return isNaN(n) ? 0 : n;
}

// Dynamic fee detection — do NOT hardcode individual fee type names here.
// The API already flags fee rows with isFee: "Yes" (Purchase Fee, Purchase
// Auth Fee, Settlement Fee, Refund Fee, Chargeback Fee, and any fee type
// added in future all set this). The transactionType-suffix check is kept
// only as a fallback in case isFee is ever missing/blank on a fee row.
function isFeeRecord(r) {
	if (String(r.isFee || "").trim().toLowerCase() === "yes") return true;
	// The VAT Fee row on a Settlement is flagged isFee: "No" by the API even
	// though it is one — catch it explicitly by type + subtype.
	if (
		String(r.transactionType || "").trim() === "Settlement" &&
		String(r.transactionSubType || "").trim() === "VAT Fee"
	) return true;
	return /fee$/i.test(String(r.transactionType || "").trim());

}

// Buckets each fee row into one of the four report categories the user
// wants split out. "Purchase" deliberately covers BOTH "Purchase Fee" and
// "Purchase Auth Fee" (they belong in the same table). Any fee type that
// doesn't match one of the known buckets falls into "other" so it still
// shows up somewhere instead of silently vanishing if a new fee type is
// introduced later.
function feeCategoryOf(r) {
	const t = String(r.transactionType || "").trim();
	if (t === "Purchase Fee" || t === "Purchase Auth Fee") return "purchase";
	if (t === "Refund Fee") return "refund";
	if (t === "Chargeback Fee") return "chargeback";
	// The API labels the settlement fee row's transactionType as plain
	// "Settlement" (statusDescription "Settlement Fee") rather than
	// "Settlement Fee" like the other fee types — match both spellings.
	if (t === "Settlement") return "settlement";
	return "other";
}

function fmtMoney(n, currencySymbol) {
	return (currencySymbol || "") + Number(n || 0).toLocaleString("en-GB", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	});
}

// Groups raw transactions into the original "Report Table" shape
// (Transaction Type / SubType / Currency / Count / Total Amount).
function groupTransactions(records) {
	const groups = {};
	records.forEach((r) => {
		const key = [r.transactionType, r.transactionSubType, r.currency].join("|");
		if (!groups[key]) {
			groups[key] = {
				transactionType: r.transactionType,
				transactionSubType: r.transactionSubType,
				currency: r.currency,
				noOfTransaction: 0,
				transactionAmount: 0
			};
		}
		groups[key].noOfTransaction += 1;
		groups[key].transactionAmount += num(r.amount);
	});
	return Object.values(groups);
}


// Builds the full dashboard summary (Payments Collected, Volume, P&L,
// completed / attempted-incomplete / failed breakdowns, and fee revenue
// broken out into one table per fee category).
function buildDashboardSummary(records) {
	const purchases = records.filter((r) => String(r.transactionType || "").trim().toUpperCase() === "PURCHASE");
	const fees = records.filter((r) => {
		return isFeeRecord(r);
	});

	// Lookup of every non-fee transaction by its own transactionId, so a fee
	// row can be traced back to whichever transaction it was charged against
	// (a Purchase, a Refund, a Chargeback, a Settlement, or any future type) —
	// that parent's own paymentMode/paymentBrand is what determines which
	// "Method" row the fee revenue belongs under.
	const recordById = {};
	records.forEach((r) => {
		if (!isFeeRecord(r) && r.transactionId) recordById[r.transactionId] = r;
	});

	const feeByParent = {};
	fees.forEach((f) => {
		const pid = f.parentId;
		if (pid === null || pid === undefined || pid === "" || pid === "-") return;
		feeByParent[pid] = (feeByParent[pid] || 0) + num(f.amount);
	});

	purchases.forEach((p) => {
		p.__fee = feeByParent[p.transactionId] || 0;
		p.__domestic = isDomestic(p.consumerCountry);
		p.__method = methodLabel(p);
		p.__amount = num(p.amount);
	});

	// "Success" is the normal completed state; "Settled" is what a purchase
	// moves to once it's been through settlement — both count as completed,
	// otherwise settled transactions vanish from every table.
	// Compared case-insensitively — the API isn't consistent about casing
	// (e.g. "Success" vs "success"), and a case-sensitive match was
	// silently dropping otherwise-completed purchases into "Attempted /
	// Incomplete" whenever the casing didn't match exactly.
	const statusOf = (r) => String(r.status || "").trim().toLowerCase();
	const COMPLETED_STATUSES = ["success", "settled"];
	const completedRaw = purchases
		.filter((p) => COMPLETED_STATUSES.includes(statusOf(p)))
		.slice()
		.sort((a, b) => (toDate(a.successTime || a.registeredTime) || 0) - (toDate(b.successTime || b.registeredTime) || 0));

	// Every non-completed purchase attempt (Pending, Processing, Failed,
	// Cancelled, ...). Kept together for the summary-card totals below,
	// then split into "Failed" and "Attempted / Incomplete" for the two
	// separate tables the report shows.
	const notCompletedRaw = purchases
		.filter((p) => !COMPLETED_STATUSES.includes(statusOf(p)))
		.slice();

	const failedRaw = notCompletedRaw
		.filter((p) => statusOf(p) === "failed")
		.slice()
		.sort((a, b) => (toDate(a.registeredTime) || 0) - (toDate(b.registeredTime) || 0));

	const incompleteRaw = notCompletedRaw
		.filter((p) => statusOf(p) !== "failed")
		.slice()
		.sort((a, b) => (toDate(a.registeredTime) || 0) - (toDate(b.registeredTime) || 0));

	const completed = completedRaw.map((r) => ({
		date: fmtDateTime(r.successTime || r.registeredTime),
		student: r.consumerName || r.toCustName || "",
		origin: r.__domestic ? "GB (Domestic)" : `${r.consumerCountry || "Unknown"} (Non-domestic)`,
		method: r.__method,
		amount: r.__amount,
		fee: r.__fee,
		ref: r.paymentReference || r.orderDescription || "",
		currency: r.currency
	}));

	const mapAttempt = (r) => ({
		date: fmtDateTime(r.registeredTime),
		student: r.consumerName || r.toCustName || "",
		origin: r.__domestic ? "GB" : (r.consumerCountry || "Unknown"),
		method: r.__method,
		amount: r.__amount,
		status: r.status || "",
		remark: r.remark || "",
		currency: r.currency
	});

	const incomplete = incompleteRaw.map(mapAttempt);
	const failed = failedRaw.map(mapAttempt);

	// Fee Revenue tables — one table per fee category (Purchase, which
	// includes both "Purchase Fee" and "Purchase Auth Fee" / Refund /
	// Chargeback / Settlement), each broken down by the payment method of
	// the transaction the fee was charged against. "other" catches any
	// fee type that doesn't match a known category so nothing is silently
	// dropped if a new fee type is introduced later.
	const buildFeeTable = (categoryFees) => {
		const groups = {};
		categoryFees.forEach((f) => {
			const parent = recordById[f.parentId];
			const key = parent ? methodLabel(parent) : methodLabel(f);
			if (!groups[key]) groups[key] = { methodLabel: key, count: 0, feeRevenue: 0 };
			groups[key].count += 1;
			groups[key].feeRevenue += num(f.amount);
		});
		return Object.values(groups).sort((a, b) => b.feeRevenue - a.feeRevenue);
	};

	// Settlement Fee Revenue table needs its own labeling logic instead of the
	// generic buildFeeTable: label by the fee row's own transactionType, except
	// a VAT Fee (transactionSubType === "VAT Fee") is broken out as its own
	// distinct row rather than merged under "Settlement".
	const buildSettlementFeeTable = (categoryFees) => {
		const groups = {};
		categoryFees.forEach((f) => {
			const subType = String(f.transactionSubType || "").trim();
			const key = subType === "VAT Fee" ? "VAT Fee" : (f.transactionType || "Settlement");
			if (!groups[key]) groups[key] = { methodLabel: key, count: 0, feeRevenue: 0 };
			groups[key].count += 1;
			groups[key].feeRevenue += num(f.amount);
		});
		return Object.values(groups).sort((a, b) => b.feeRevenue - a.feeRevenue);
	};

	const purchaseFees = fees.filter((f) => feeCategoryOf(f) === "purchase");
	const refundFees = fees.filter((f) => feeCategoryOf(f) === "refund");
	const chargebackFees = fees.filter((f) => feeCategoryOf(f) === "chargeback");
	const settlementFees = fees.filter((f) => feeCategoryOf(f) === "settlement");
	const otherFees = fees.filter((f) => feeCategoryOf(f) === "other");

	const feeTables = {
		purchase: buildFeeTable(purchaseFees),
		refund: buildFeeTable(refundFees),
		chargeback: buildFeeTable(chargebackFees),
		// Other fee types (anything not Purchase/Refund/Chargeback) now live in
		// the same table as Settlement, all labeled "Settlement" as the method.
		settlement: buildSettlementFeeTable(settlementFees)

	};

	const totalVolume = completedRaw.reduce((s, r) => s + r.__amount, 0);
	const totalFee = fees.reduce((s, f) => s + num(f.amount), 0);
	const domesticCount = completedRaw.filter((r) => r.__domestic).length;
	const nonDomesticCount = completedRaw.length - domesticCount;
	const attemptedValue = notCompletedRaw.reduce((s, r) => s + r.__amount, 0);

	const allDates = purchases.map((p) => toDate(p.registeredTime)).filter(Boolean);
	const periodStart = allDates.length ? new Date(Math.min(...allDates)) : null;
	const periodEnd = allDates.length ? new Date(Math.max(...allDates)) : null;

	const currencySymbol = completedRaw[0]?.currency || incompleteRaw[0]?.currency || failedRaw[0]?.currency || "";

	return {
		completed,
		incomplete,
		failed,
		feeTables,
		currencyLabel: currencySymbol,
		summary: {
			paymentsCollected: completedRaw.length,
			totalVolume,
			pnlGross: totalFee,
			domestic: domesticCount,
			nonDomestic: nonDomesticCount,
			attemptedCount: notCompletedRaw.length,
			attemptedValue,
			periodStart: periodStart ? fmtDate(periodStart) : null,
			periodEnd: periodEnd ? fmtDate(periodEnd) : null
		}
	};
}

const SummaryReport = () => {
	var date = new Date();
	const [selectedDate, setSelectedDate] = useState(
		new Date(date.getFullYear(), date.getMonth(), 1)
	);
	const Theme = useSelector((state) => state.theme.theme);

	const handleDateChange = (date) => {
		setSelectedDate(date);
		if (date > selectedToDate) {
			setSelectedToDate(date);
		}
	};
	const [selectedToDate, setSelectedToDate] = useState(new Date());
	const handleToDateChange = (date) => {
		setSelectedToDate(date);
	};
	const [customerId, setCustomerId] = useState("");
	const [transactionType, setTransactionType] = useState("");
	const [currency, setCurrency] = useState("");
	const [transactionSubType, setTransactionSubType] = useState("");
	const [getTransactionTypeListResArray, setGetTransactionTypeListResArray] = useState([]);
	const [getSummeryReportListStatus, setGetSummeryReportListStatus] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [searchLoading, setSearchLoading] = useState(false);
	const [isPdfLoading, setIsPdfLoading] = useState(false);
	const [searchError, setSearchError] = useState("");

	const [getSummeryReportListResArry, setgetSummeryReportListResArry] = useState([]);
	const [dashboardData, setDashboardData] = useState(null);

	const [getTransactionSubTypeListResArray, setGetTransactionSubTypeListResArray] = useState([]);
	const [transactionSubTypesLists, getSetTransactionTypesLists] = useState(false);
	const [timestamp, setTimestamp] = useState("");

	const dispatch = useDispatch();
	const store = useStore();
	const dashboardRef = useRef(null);

	const regenerateTokenRes = useSelector(({ regenerateToken }) => regenerateToken);
	var authToken = regenerateTokenRes.data.AuthToken;

	const getCustomerIdAndNameListRes = useSelector(
		({ getCustomerIdAndNameList }) => getCustomerIdAndNameList
	);

	const getTransactionCurrencyListRes = useSelector(
		({ getTransactionCurrencyList }) => getTransactionCurrencyList
	);

	const login = useSelector((state) => state.login);

	const getTransactionSubTypesRes = useSelector(
		({ partnerTransactionSubTypesLists }) => partnerTransactionSubTypesLists
	);

	const getTransactionSubTypeListReqApi = () => {
		getSetTransactionTypesLists(true);
		const req = {
			actionCustomerMasterRequestVO: {
				walletOwnerId: login.data.subRole === "" ? login.data.partnerId : login.data.parentId,
				transactionType: transactionType
			}
		};
		return dispatch(partnerTransactionSubTypesListReq(req, authToken));
	};

	useEffect(() => {
		if (transactionType) {
			getTransactionSubTypeListReqApi();
		}
	}, [transactionType]);

	useEffect(() => {
		setTransactionSubType("");
		setGetTransactionSubTypeListResArray([]);
	}, [transactionType]);

	useEffect(() => {
		if (transactionSubTypesLists) {
			if (getTransactionSubTypesRes.data.status === "Success") {
				setGetTransactionSubTypeListResArray(
					getTransactionSubTypesRes.data.transactionSubtype
				);
			}
			if (
				getTransactionSubTypesRes.data.status === "Failed" ||
				getTransactionSubTypesRes.data.status === "failed"
			) {
				setGetTransactionSubTypeListResArray([]);
			}
		}
	}, [getTransactionSubTypesRes]);

	const getTransactionCurrencyListFunc = () => {
		const requestData = {
			loginDetails: {
				walletOwnerId: login.data.subRole === "" ? login.data.partnerId : login.data.parentId,
			}
		};
		dispatch(getTransactionCurrencyListAction(requestData, authToken));
	};

	useEffect(() => {
		getTransactionCurrencyListFunc();
	}, [regenerateTokenRes.data.status]);

	const handleChange = ({ target: { name, value } }) => {
		if (name === "customerId") {
			setCustomerId(value);
		}
		if (name === "transactionType") {
			const selectedValue = value === "All" ? "" : value;
			setTransactionType(selectedValue);
		}
		if (name === "currency") {
			setCurrency(value === "Select Currency" ? "" : value);
		}
		if (name === "transactionSubType") {
			setTransactionSubType(value === "All" ? "" : value);
		}
		if (name === "timestamp") {
			setTimestamp(value == "All" ? "" : value);
		}
	};

	const getTransactionTypeListReqApi = () => {
		const getTransactionTypeListReq = {
			loginDetails: {
				walletOwnerId: login.data.subRole === "" ? login.data.partnerId : login.data.parentId,
			}
		};
		return dispatch(
			getTransactionTypeListDetails(getTransactionTypeListReq, authToken)
		);
	};

	useEffect(() => {
		getTransactionTypeListReqApi();
	}, [regenerateTokenRes.data.status]);

	const getTransactionTypeListRes = useSelector(
		({ getTransactionTypeList }) => getTransactionTypeList
	);

	const getTransactionTypeList = () => {
		if (getTransactionTypeListRes.data.status == "success") {
			setGetTransactionTypeListResArray(
				getTransactionTypeListRes.data.transactionType
			);
		}
	};

	useEffect(() => {
		getTransactionTypeList();
	}, [getTransactionTypeListRes.data.status]);

	useEffect(() => {
		if (regenerateTokenRes.data.status === "success") {
			const getCustomerIdAndNameReq = {
				loginDetails: {
					walletOwnerId: login.data.subRole === "" ? login.data.partnerId : login.data.parentId,
				},
				actionCustomerMasterRequestVO: {
					customerNameOrId: ""
				}
			};
			dispatch(getCustomerIdAndNameList(getCustomerIdAndNameReq, authToken));
		}
	}, [regenerateTokenRes.data.status]);

	const filterOptionsL = (options, { inputValue }) => {
		if (inputValue.length < 3) {
			return [];
		}
		const filterOptionsList = options.filter((option) => option.includes(inputValue) || "");
		return filterOptionsList.slice(0, 20);
	};

	// =====================================================================
	// Fetch getWalletTransactionList matching the current filters.
	// Single call — no pagination loop.
	// =====================================================================
	// const fetchAllWalletTransactions = async (baseReqVO) => {
	// 	await dispatch(
	// 		getWalletTransactionList(
	// 			{
	// 				walletTransactionMasterDetailsRequestVO: {
	// 					...baseReqVO,
	// 				}
	// 			},
	// 			authToken
	// 		)
	// 	);

	// 	const state = store.getState().getWalletTransactionList;
	// 	const data = state && state.data;

	// 	if (!data || !data.status) return [];
	// 	if (String(data.status).toLowerCase() === "not found") return [];

	// 	if (String(data.status).toLowerCase() !== "success") {
	// 		if (data.description === "session out") {
	// 			SOLogout(dispatch, authToken, regenerateTokenRes.data.partnerId);
	// 		}
	// 		if (data.description === "Authorization failed") {
	// 			regenerateToken(dispatch, authToken, regenerateTokenRes.data.partnerId);
	// 		}
	// 		throw new Error(data.description || `Request failed with status "${data.status}"`);
	// 	}

	// 	return [].concat(data.transactionList || []);
	// };

	const fetchAllWalletTransactions = async (baseReqVO) => {
		const attemptFetch = async (tokenToUse) => {
			await dispatch(
				getWalletTransactionList(
					{ walletTransactionMasterDetailsRequestVO: { ...baseReqVO } },
					tokenToUse
				)
			);
			return store.getState().getWalletTransactionList;
		};

		let state = await attemptFetch(authToken);
		let data = state && state.data;

		if (data && String(data.status).toLowerCase() !== "success") {
			if (data.description === "session out") {
				SOLogout(dispatch, authToken, regenerateTokenRes.data.partnerId);
				throw new Error(data.description);
			}
			if (data.description === "Authorization failed") {
				// Wait for the token regeneration to actually complete before retrying.
				await regenerateToken(dispatch, authToken, regenerateTokenRes.data.partnerId);
				const refreshedToken = store.getState().regenerateToken.data.AuthToken;

				state = await attemptFetch(refreshedToken);
				data = state && state.data;

				if (!data || String(data.status).toLowerCase() !== "success") {
					throw new Error(data?.description || "Authorization failed after token refresh.");
				}
			} else {
				throw new Error(data.description || `Request failed with status "${data.status}"`);
			}
		}

		if (!data || !data.status) return [];
		if (String(data.status).toLowerCase() === "not found") return [];
		return [].concat(data.transactionList || []);
	};
	// Guards against a slow, superseded request clobbering a newer one's
	// results — if the user hits Search again (or changes filters and
	// re-searches) before the first ~1-min-long request finishes, only the
	// LATEST request's response is allowed to update state.
	const searchRequestIdRef = useRef(0);

	const handleSearch = async () => {
		if (!selectedDate || !selectedToDate) {
			alert("Please enter a Date..!!");
			return;
		}

		const requestId = ++searchRequestIdRef.current;

		setIsLoading(true);
		setSearchLoading(true);
		setGetSummeryReportListStatus(true);
		setSearchError("");
		setDashboardData(null);
		setgetSummeryReportListResArry([]);

		try {
			const baseReqVO = {
				walletOwnerId: login.data.subRole === "" ? login.data.partnerId : login.data.parentId,
				currency: currency,
				fromDate: moment(selectedDate).format("DD/MM/YYYY"),
				toDate: moment(selectedToDate).format("DD/MM/YYYY"),
				walletCustomerId: customerId.split("-")[0] || "",
				transactionType: transactionType,
				transactionSubType: transactionSubType,
				timestamp: timestamp,
			};

			const records = await fetchAllWalletTransactions(baseReqVO);

			// A newer search started while this one was still in flight — drop
			// this response instead of overwriting the newer results with stale data.
			if (requestId !== searchRequestIdRef.current) return;

			if (!records.length) {
				setgetSummeryReportListResArry([]);
				setDashboardData(null);
			} else {
				setgetSummeryReportListResArry(groupTransactions(records));
				setDashboardData(buildDashboardSummary(records));
			}
		} catch (err) {
			if (requestId !== searchRequestIdRef.current) return;
			console.error(err);
			setSearchError(err.message || "Something went wrong while fetching transactions.");
		} finally {
			if (requestId === searchRequestIdRef.current) {
				setIsLoading(false);
				setSearchLoading(false);
			}
		}
	};

	// =====================================================================
	// Downloads
	// =====================================================================
	const reportBaseName = () => {
		const parts = ["Wallet_Summary_Report"];
		if (login.data.subRole === "" ? login.data.partnerId : login.data.parentId) parts.push("Owner" + login.data.subRole === "" ? login.data.partnerId : login.data.parentId);
		parts.push(moment(selectedDate).format("DDMMYYYY"));
		parts.push(moment(selectedToDate).format("DDMMYYYY"));
		return parts.join("_");
	};

	const handleDownloadPdf = async () => {
		if (!dashboardData) return;
		setIsPdfLoading(true);

		try {
			const jsPDF = await loadJsPdf();
			const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
			const pageWidth = doc.internal.pageSize.getWidth();
			const marginX = 40;
			const usableWidth = pageWidth - marginX * 2;
			let y = 46;

			const s = dashboardData.summary;
			const cur = dashboardData.currencyLabel || "";

			// Palette lifted straight from SummaryReportDashboard.css so the PDF
			// reads as the same document as the on-screen dashboard, not a
			// generic exported table.
			const C = {
				text: [26, 26, 26],       // #1a1a1a
				grey: [107, 107, 107],    // #6b6b6b
				greyLight: [140, 140, 140], // #8c8c8c
				border: [217, 217, 217],  // #d9d9d9
				headerBg: [242, 242, 242],// #f2f2f2
				stripeBg: [247, 247, 247],// #f7f7f7
				green: [30, 122, 76],     // #1e7a4c
				highlightBg: [238, 240, 251], // #eef0fb
				highlightText: [57, 73, 160]  // #3949a0
			};

			doc.setFont("helvetica", "bold");
			doc.setFontSize(20);
			doc.setTextColor(...C.text);
			doc.text("Fee Payment Collection Dashboard", marginX, y);
			y += 20;

			doc.setFont("helvetica", "normal");
			doc.setFontSize(10);
			doc.setTextColor(...C.grey);
			doc.text(`Unipay Summary Report   |   Snapshot generated ${moment().format("DD MMMM YYYY")}`, marginX, y);
			y += 14;
			if (periodLine) {
				doc.setFont("helvetica", "italic");
				doc.setFontSize(9);
				doc.setTextColor(...C.greyLight);
				doc.text(periodLine, marginX, y);
				y += 13;
			}
			if (filterLine) {
				doc.setFont("helvetica", "normal");
				doc.setFontSize(9);
				doc.setTextColor(...C.greyLight);
				doc.text(`Filters applied: ${filterLine}`, marginX, y);
				y += 13;
			}
			y += 10;

			// ---- Summary card grid — hand-drawn to match the CSS .box-row
			// tiles (border colour, header label style, big bold value, the
			// green P&L figure, the indigo "highlight" tile) instead of a
			// plain table. ----
			const drawCardRow = (boxes, boxHeight) => {
				const bw = usableWidth / boxes.length;
				doc.setDrawColor(...C.border);
				doc.setLineWidth(0.75);
				doc.rect(marginX, y, usableWidth, boxHeight);
				boxes.forEach((b, i) => {
					const bx = marginX + i * bw;
					if (i > 0) doc.line(bx, y, bx, y + boxHeight);
					if (b.fill) {
						doc.setFillColor(...b.fill);
						doc.rect(bx + 0.75, y + 0.75, bw - 1.5, boxHeight - 1.5, "F");
					}
					doc.setFont("helvetica", "bold");
					doc.setFontSize(7.5);
					doc.setTextColor(...(b.labelColor || C.greyLight));
					doc.text(b.label.toUpperCase(), bx + bw / 2, y + 20, { align: "center", charSpace: 0.4 });

					doc.setFont("helvetica", "bold");
					doc.setFontSize(b.small ? 10 : 16);
					doc.setTextColor(...(b.valueColor || C.text));
					doc.text(String(b.value), bx + bw / 2, y + boxHeight / 2 + 12, { align: "center" });
				});
				y += boxHeight;
			};

			drawCardRow([
				{ label: "Payments Collected", value: s.paymentsCollected },
				{ label: "Total Volume Collected", value: `${fmtMoney(s.totalVolume)} ${cur}` },
				{ label: "Running P&L (Gross)", value: `${fmtMoney(s.pnlGross)} ${cur}`, valueColor: C.green },
				{ label: "Domestic / Non-domestic", value: `${s.domestic} / ${s.nonDomestic}` }
			], 66);

			drawCardRow([
				{ label: "Outstanding Remaining", value: "—", small: true, valueColor: [187, 187, 187] },
				{ label: "Attempted / Incomplete", value: s.attemptedCount },
			], 62);

			y += 14;
			doc.setFont("helvetica", "normal");
			doc.setFontSize(8);
			doc.setTextColor(...C.grey);
			const note = doc.splitTextToSize(
				`P&L shown gross (fee revenue only) — underlying processor/gateway costs are not netted off. ` +
				`"Attempted / Incomplete" covers Pending, Processing and Cancelled purchase attempts — Failed ` +
				`attempts are listed in their own table below (total attempted value ${fmtMoney(s.attemptedValue)} ${cur} ` +
				`across all non-completed attempts). Domestic = United Kingdom (GBR/GB); Non-domestic = all other ` +
				`student countries. Outstanding Remaining is left blank — this data is not returned by the Transaction Data.`,
				usableWidth
			);
			doc.text(note, marginX, y);
			y += note.length * 10 + 16;

			const sectionHeader = (title) => {
				doc.setFont("helvetica", "bold");
				doc.setFontSize(13);
				doc.setTextColor(...C.text);
				doc.text(title, marginX, y);
				y += 10;
			};

			// Shared table theming so every table matches the CSS: light-grey
			// header row, faint zebra striping, #d9d9d9 gridlines.
			const tableTheme = {
				theme: "grid",
				styles: { font: "helvetica", fontSize: 8, cellPadding: 5, textColor: C.text, lineColor: C.border, lineWidth: 0.6, overflow: "ellipsize" },
				headStyles: { fillColor: C.headerBg, textColor: C.text, fontStyle: "bold", lineColor: C.border, lineWidth: 0.6, halign: "left" },
				alternateRowStyles: { fillColor: C.stripeBg },
				footStyles: { fillColor: C.headerBg, textColor: C.text, fontStyle: "bold", lineColor: C.border, lineWidth: 0.6 }
			};

			// Completed Payments — column widths sized to sum to the portrait
			// usable width (~515pt). Long free-text values (Student/Method/
			// Reference) ellipsize instead of stretching the column, which is
			// what previously crushed every other column down to nothing.
			sectionHeader(`Completed Payments (${(dashboardData.completed || []).length})`);
			doc.autoTable({
				...tableTheme,
				startY: y,
				margin: { left: marginX, right: marginX },
				tableWidth: "wrap",
				head: [["Date", "Student", "Origin", "Method", "Amount", "Applied Fee", "Payment Reference"]],
				body: (dashboardData.completed || []).length
					? (dashboardData.completed || []).map((r) => [
						r.date, r.student, r.origin, r.method,
						`${fmtMoney(r.amount)} ${r.currency}`,
						`${fmtMoney(r.fee)} ${r.currency}`,
						r.ref
					])
					: [["No completed payments found", "", "", "", "", "", ""]],
				columnStyles: {
					0: { cellWidth: 52 },
					1: { cellWidth: 68 },
					2: { cellWidth: 62 },
					3: { cellWidth: 68 },
					4: { cellWidth: 55, halign: "right" },
					5: { cellWidth: 65, halign: "right" },   // was 55 — was clipping "Applied Fee"
					6: { cellWidth: 145 }                    // was 140, adjusted to keep total ≈515pt
				}
			});
			y = doc.lastAutoTable.finalY + 20;

			// Attempted / Incomplete Payments (excludes Failed — that's its own table below)
			sectionHeader(`Attempted / Incomplete Payments (${(dashboardData.incomplete || []).length})`);
			doc.autoTable({
				...tableTheme,
				startY: y,
				margin: { left: marginX, right: marginX },
				tableWidth: "wrap",
				head: [["Date", "Student", "Origin", "Method", "Attempted Amount", "Status", "Remark / Notes"]],
				body: (dashboardData.incomplete || []).length
					? (dashboardData.incomplete || []).map((r) => [
						r.date, r.student, r.origin, r.method,
						`${fmtMoney(r.amount)} ${r.currency}`,
						r.status, r.remark || "—"
					])
					: [["No attempted/incomplete payments found", "", "", "", "", "", ""]],
				columnStyles: {
					0: { cellWidth: 62 },
					1: { cellWidth: 68 },
					2: { cellWidth: 60 },
					3: { cellWidth: 68 },
					4: { cellWidth: 62, halign: "right" },
					5: { cellWidth: 45 },
					6: { cellWidth: 150 }
				}
			});
			y = doc.lastAutoTable.finalY + 20;

			// Failed Payments — its own table, split out from Attempted / Incomplete.
			sectionHeader(`Failed Payments (${(dashboardData.failed || []).length})`);
			doc.autoTable({
				...tableTheme,
				startY: y,
				margin: { left: marginX, right: marginX },
				tableWidth: "wrap",
				head: [["Date", "Student", "Origin", "Method", "Attempted Amount", "Status", "Remark / Notes"]],
				body: (dashboardData.failed || []).length
					? (dashboardData.failed || []).map((r) => [
						r.date, r.student, r.origin, r.method,
						`${fmtMoney(r.amount)} ${r.currency}`,
						r.status, r.remark || "—"
					])
					: [["No failed payments found", "", "", "", "", "", ""]],
				columnStyles: {
					0: { cellWidth: 62 },
					1: { cellWidth: 68 },
					2: { cellWidth: 60 },
					3: { cellWidth: 68 },
					4: { cellWidth: 62, halign: "right" },
					5: { cellWidth: 45 },
					6: { cellWidth: 150 }
				}
			});
			y = doc.lastAutoTable.finalY + 20;

			// Fee Revenue — one table per fee category, each broken down by
			// payment method, instead of a single combined table.
			const drawFeeTable = (title, rows) => {
				sectionHeader(title);
				const totalCount = rows.reduce((sum, r) => sum + r.count, 0);
				const totalRev = rows.reduce((sum, r) => sum + r.feeRevenue, 0);
				doc.autoTable({
					...tableTheme,
					startY: y,
					margin: { left: marginX, right: marginX },
					head: [["Method", "Fee Count", "Fee Revenue"]],
					body: rows.length
						? rows.map((r) => [r.methodLabel, String(r.count), `${fmtMoney(r.feeRevenue)} ${cur}`])
						: [["No data", "", ""]],
					foot: [["Total", String(totalCount), `${fmtMoney(totalRev)} ${cur}`]],
					columnStyles: { 1: { halign: "left" }, 2: { halign: "lefr" } }
				});
				y = doc.lastAutoTable.finalY + 20;
			};

			drawFeeTable("Transaction Fee Revenue by Payment Method", (dashboardData.feeTables?.purchase || []));
			drawFeeTable("Refund Fee Revenue by Payment Method", (dashboardData.feeTables?.refund || []));
			drawFeeTable("Chargeback Fee Revenue by Payment Method", (dashboardData.feeTables?.chargeback || []));
			drawFeeTable("Settlement Fee Revenue", (dashboardData.feeTables?.settlement || []));
			if ((dashboardData.feeTables?.other || []).length) {
				drawFeeTable("Other Fee Revenue by Payment Method", (dashboardData.feeTables?.other || []));
			}

			doc.setFont("helvetica", "normal");
			doc.setFontSize(7.5);
			doc.setTextColor(...C.grey);
			const footerText = doc.splitTextToSize(
				"Prepared directly from the Transaction Data for the filters applied above.",
				usableWidth
			);
			doc.text(footerText, marginX, y);

			// Page numbers
			const pageCount = doc.internal.getNumberOfPages();
			for (let i = 1; i <= pageCount; i++) {
				doc.setPage(i);
				doc.setFontSize(8);
				doc.setTextColor(150);
				doc.text(`Page ${i} of ${pageCount}`, pageWidth - marginX - 40, doc.internal.pageSize.getHeight() - 20);
			}

			doc.save(reportBaseName() + ".pdf");
		} catch (err) {
			console.error(err);
			setSearchError(err.message || "PDF generation failed.");
		} finally {
			setIsPdfLoading(false);
		}
	};

	const periodLine = useMemo(() => {

		if (!dashboardData) return "";
		const s = dashboardData.summary;
		if (s.periodStart && s.periodEnd) {
			return `Transaction activity covering ${s.periodStart} – ${s.periodEnd}`;
		}
		return "";
	}, [dashboardData]);

	const filterLine = useMemo(() => {
		const bits = [];
		if (login.data.subRole === "" ? login.data.partnerId : login.data.parentId) bits.push(`Owner ID ${login.data.subRole === "" ? login.data.partnerId : login.data.parentId}`);
		if (customerId) bits.push(`Customer ${customerId}`);
		if (transactionType) bits.push(`Type: ${transactionType}`);
		if (transactionSubType) bits.push(`SubType: ${transactionSubType}`);
		if (currency) bits.push(currency);
		return bits.join(" · ");
	}, [customerId, transactionType, transactionSubType, currency, regenerateTokenRes]);

	// Renders one "Fee Revenue by Payment Method" table for a single fee
	// category (Purchase / Refund / Chargeback / Settlement / Other).
	const renderFeeTable = (title, rows) => {
		const totalCount = rows.reduce((s, r) => s + r.count, 0);
		const totalRev = rows.reduce((s, r) => s + r.feeRevenue, 0);
		return (
			<>
				<h2>{title}</h2>
				<table>
					<thead>
						<tr>
							<th style={{ textAlign: "left" }}>Method</th>
							<th className="num" style={{ textAlign: "left" }}>Fee Count</th>
							<th className="num" style={{ textAlign: "left" }}>Fee Revenue</th>
						</tr>
					</thead>
					<tbody>
						{rows.length ? (
							rows.map((r, i) => (
								<tr key={i}>
									<td style={{ textAlign: "left" }}>{r.methodLabel}</td>
									<td className="num" style={{ textAlign: "left" }}>{r.count}</td>
									<td className="num" style={{ textAlign: "left" }}>{fmtMoney(r.feeRevenue)} {dashboardData.currencyLabel}</td>
								</tr>
							))
						) : (
							<tr><td colSpan="3" className="empty-state">No data</td></tr>
						)}
					</tbody>
					<tfoot>
						<tr>
							<td style={{ textAlign: "left" }}>Total</td>
							<td className="num" style={{ textAlign: "left" }}>{totalCount}</td>
							<td className="num" style={{ textAlign: "left" }}>{fmtMoney(totalRev)} {dashboardData.currencyLabel}</td>
						</tr>
					</tfoot>
				</table>
			</>
		);
	};

	return (
		<>
			<Layout>
				<Card>
					<Grid container spacing={2} style={{ padding: "25px", marginTop: "2rem" }}>
						<Grid item xs={12} sm={12} md={3} lg={3}>
							<FormControl fullWidth style={{ marginTop: "5px" }}>
								<LocalizationProvider dateAdapter={AdapterDateFns}>
									<DatePicker
										label="From Date"
										format="MM/dd/yyyy"
										value={selectedDate}
										onChange={handleDateChange}
										disableFuture={true}
									/>
								</LocalizationProvider>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={12} md={3} lg={3}>
							<FormControl fullWidth style={{ marginTop: "5px" }}>
								<LocalizationProvider dateAdapter={AdapterDateFns}>
									<DatePicker
										label="To Date"
										format="MM/dd/yyyy"
										value={selectedToDate}
										onChange={handleToDateChange}
										disableFuture={true}
										minDate={selectedDate}
									/>
								</LocalizationProvider>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={12} md={3} lg={3}>
							<TextField
								select
								autoFocus
								margin="dense"
								id="timestamp"
								label="Time Stamp"
								type="name"
								fullWidth
								name="timestamp"

								// value={timestamp || ""}
								defaultValue={"All" || timestamp}
								onChange={handleChange}
								variant="standard"

							>
								<MenuItem value="All" selected="All	">
									Select Time Stamp
								</MenuItem>
								{/* <MenuItem value="Select Timestamp">
																select Time Stamp
															</MenuItem> */}
								<MenuItem value="CreationTime">CreationTime</MenuItem>
								<MenuItem value="SuccessTime" >SuccessTime</MenuItem>
								<MenuItem value="UpdationTime" >UpdateTime</MenuItem>

							</TextField>
						</Grid>

						<Grid item xs={12} sm={12} md={3} lg={3}>
							<Autocomplete
								id="combo-box-demo"
								options={[].concat(getCustomerIdAndNameListRes.data.currencyList || [""])}
								getOptionLabel={(option) => option}
								filterOptions={filterOptionsL}
								value={customerId}
								onChange={handleChange}
								onInputChange={(event, newInputValue) => {
									setCustomerId(newInputValue);
								}}
								renderInput={(params) => (
									<TextField
										autoFocus
										margin="dense"
										id="name"
										label="Customer ID*"
										style={{ marginTop: "3px" }}
										type="tel"
										fullWidth
										{...params}
										name="customerId"
										variant="standard"
										sx={{
											"& .MuiInput-underline:before": { borderBottomColor: "gray" },
											"& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottomColor: "gray" },
											"& .MuiInput-underline:after": { borderBottomColor: "gray" }
										}}
									/>
								)}
							/>
						</Grid>

						<Grid xs={12} item sm={12} md={3} lg={3}>
							<TextField
								select
								autoFocus
								margin="dense"
								id="name"
								label="Transaction Type"
								type="name"
								fullWidth
								variant="standard"
								name="transactionType"
								defaultValue={"All" || transactionType}
								onChange={handleChange}
								InputProps={{
									sx: {
										"&:before": { borderBottomColor: "gray" },
										"&:after": { borderBottomColor: "gray" }
									}
								}}
								InputLabelProps={{ sx: { color: "gray" } }}
							>
								<MenuItem value="All" selected="All">All</MenuItem>
								{(() => {
									if (getTransactionTypeListResArray) {
										return [].concat(getTransactionTypeListResArray).map((c) => (
											<MenuItem value={c} key={c}>
												{c === "Purchase" ? "Transaction" : c}
											</MenuItem>
										));
									}
									return <MenuItem>No Data</MenuItem>;
								})()}
							</TextField>
						</Grid>

						<Grid xs={12} item sm={12} md={3} lg={3}>
							<TextField
								select
								autoFocus
								margin="dense"
								id="name"
								label="Transaction SubType"
								type="name"
								fullWidth
								variant="standard"
								name="transactionSubType"
								defaultValue={"All" || transactionSubType}
								onChange={handleChange}
								InputProps={{
									sx: {
										"&:before": { borderBottomColor: "gray" },
										"&:after": { borderBottomColor: "gray" }
									}
								}}
								InputLabelProps={{ sx: { color: "gray" } }}
							>
								<MenuItem value="All" selected="All">All</MenuItem>
								{(() => {
									if (getTransactionSubTypeListResArray) {
										return [].concat(getTransactionSubTypeListResArray).map((c) => (
											<MenuItem value={c} key={c}>
												{c === "Wallet" ? "APM" : c}
											</MenuItem>
										));
									}
									return <MenuItem>No Data</MenuItem>;
								})()}
							</TextField>
						</Grid>

						<Grid xs={12} item sm={12} md={3} lg={3}>
							<TextField
								select
								autoFocus
								margin="dense"
								id="name"
								label="Currency*"
								type="name"
								fullWidth
								name="currency"
								defaultValue={"Select Currency" || currency}
								onChange={handleChange}
							>
								<MenuItem value="Select Currency" selected="Select Currency">Select Currency</MenuItem>
								{getTransactionCurrencyListRes?.success &&
									getTransactionCurrencyListRes?.data?.status === "success" &&
									[].concat(getTransactionCurrencyListRes.data.currencyList || []).map((x) => (
										<MenuItem value={x} key={x}>{x}</MenuItem>
									))}
							</TextField>
						</Grid>

						<Grid xs={12} item sm={12} md={3} lg={3}>
							<CustomButton
								style={{ padding: "8px" }}
								onClick={handleSearch}
								disabled={searchLoading}
							>
								{searchLoading ? (
									<>
										Searching…&nbsp;
										<span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
									</>
								) : (
									<>Search <SearchIcon /></>
								)}
							</CustomButton>
						</Grid>
					</Grid>
				</Card>

				{isLoading ? (
					<LoadingSpinner />
				) : searchError ? (
					<Card>
						<Grid container style={{ padding: "10px" }}>
							<Grid xs={12} sm={12} md={12} lg={12}>
								<Alert className="my-3" color="danger">{searchError}</Alert>
							</Grid>
						</Grid>
					</Card>
				) : !getSummeryReportListStatus || !dashboardData ? (
					<Card>
						<Grid container style={{ padding: "10px" }}>
							<Grid xs={12} sm={12} md={12} lg={12}>
								<Typography variant="body1" style={{ fontWeight: "700" }} className="themeText">
									Summary Report Preview
								</Typography>
							</Grid>
							<Grid xs={12} sm={12} md={12} lg={12}>
								<Alert className="my-3 tableHeaderColor" variant="secondary">
									No Records Found!
								</Alert>
							</Grid>
						</Grid>
					</Card>
				) : (
					<>
						{/* ---- Fee Payment Collection Dashboard (this is what gets exported to PDF) ---- */}
						<Card>
							<Grid
								container
								style={{ padding: "10px 10px 0", justifyContent: "flex-end" }}
							>
								<Grid item xs={4} sm={4} md={1} lg={1} style={{ textAlign: "center" }}>
									{isPdfLoading ? (
										<span
											className="spinner-border spinner-border-sm"
											role="status"
											aria-hidden="true"
											style={{ marginTop: "6px" }}
										/>
									) : (
										<img
											src={`${process.env.PUBLIC_URL}/${Theme}/pdf1.png`}
											alt="Download PDF"
											title="Download PDF"
											style={{ height: "30px", cursor: "pointer" }}
											onClick={handleDownloadPdf}
										/>
									)}
								</Grid>
							</Grid>
							<div ref={dashboardRef} className="dashboard-page">
								<h1>Fee Payment Collection Dashboard</h1>
								<p className="subtitle">Unipay Summary Report&nbsp;&nbsp;|&nbsp;&nbsp;Snapshot generated {moment().format("DD MMMM YYYY")}</p>
								{periodLine && <p className="period">{periodLine}</p>}
								{filterLine && <p className="filters-line">Filters applied: {filterLine}</p>}

								<div className="box-row row-a">
									<div className="box">
										<span className="label">Payments Collected</span>
										<span className="value">{dashboardData.summary.paymentsCollected}</span>
									</div>
									<div className="box">
										<span className="label">Total Volume Collected</span>
										<span className="value">{fmtMoney(dashboardData.summary.totalVolume, dashboardData.currencyLabel ? "" : "")} {dashboardData.currencyLabel}</span>
									</div>
									<div className="box green">
										<span className="label">Running P&amp;L (Gross)</span>
										<span className="value">{fmtMoney(dashboardData.summary.pnlGross)} {dashboardData.currencyLabel}</span>
									</div>
									<div className="box">
										<span className="label">Domestic / Non-domestic</span>
										<span className="value">{dashboardData.summary.domestic} / {dashboardData.summary.nonDomestic}</span>
									</div>
								</div>

								<div className="box-row row-b">
									<div className="box">
										<span className="label">Outstanding Remaining</span>
										<span className="value empty">—</span>
									</div>
									<div className="box">
										<span className="label">Attempted / Incomplete</span>
										<span className="value">{dashboardData.summary.attemptedCount}</span>
									</div>
								</div>

								<p className="note">
									P&amp;L shown gross (fee revenue only) — underlying processor/gateway costs are not netted off.
									"Attempted / Incomplete" covers Pending, Processing and Cancelled purchase attempts — Failed
									attempts are listed in their own table below (total attempted value {fmtMoney(dashboardData.summary.totalVolume)} {dashboardData.currencyLabel} &nbsp;
									across all non-completed attempts). Domestic = United Kingdom (GBR/GB); Non-domestic = all other
									student countries. Outstanding Remaining is left blank — this data is not returned by the Transaction Data.
								</p>

								<h2>Completed Payments ({(dashboardData.completed || []).length})</h2>
								<table>
									<thead>
										<tr>
											<th style={{ textAlign: "left" }}>Date</th><th style={{ textAlign: "left" }}>Student</th><th style={{ textAlign: "left" }}>Origin</th><th style={{ textAlign: "left" }}>Method</th>
											<th className="num" style={{ textAlign: "left" }}>Amount</th><th className="num" style={{ textAlign: "left" }}>Applied Fee</th><th style={{ textAlign: "left" }}>Payment Reference</th>
										</tr>
									</thead>
									<tbody>
										{(dashboardData.completed || []).length ? (
											(dashboardData.completed || []).map((r, i) => (
												<tr key={i}>
													<td style={{ textAlign: "left" }}>{r.date}</td>
													<td style={{ textAlign: "left" }}>{r.student}</td>
													<td style={{ textAlign: "left" }}>{r.origin}</td>
													<td style={{ textAlign: "left" }}>{r.method}</td>
													<td className="num" style={{ textAlign: "left" }}>{fmtMoney(r.amount)} {r.currency}</td>
													<td className="num" style={{ textAlign: "left" }}>{fmtMoney(r.fee)} {r.currency}</td>
													<td style={{ textAlign: "left" }}>{r.ref}</td>
												</tr>
											))
										) : (
											<tr><td colSpan="7" className="empty-state">No completed payments found</td></tr>
										)}
									</tbody>
								</table>

								<h2>Attempted / Incomplete Payments ({(dashboardData.incomplete || []).length})</h2>
								<table>
									<thead>
										<tr>
											<th style={{ textAlign: "left" }}>Date</th><th style={{ textAlign: "left" }}>Student</th><th style={{ textAlign: "left" }}>Origin</th><th style={{ textAlign: "left" }}>Method</th>
											<th className="num" style={{ textAlign: "left" }}>Attempted Amount</th><th style={{ textAlign: "left" }}>Status</th><th style={{ textAlign: "left" }}>Remark / Notes</th>
										</tr>
									</thead>
									<tbody>
										{(dashboardData.incomplete || []).length ? (
											(dashboardData.incomplete || []).map((r, i) => (
												<tr key={i}>
													<td style={{ textAlign: "left" }}>{r.date}</td>
													<td style={{ textAlign: "left" }}>{r.student}</td>
													<td style={{ textAlign: "left" }}>{r.origin}</td>
													<td style={{ textAlign: "left" }}>{r.method}</td>
													<td className="num" style={{ textAlign: "left" }}>{fmtMoney(r.amount)} {r.currency}</td>
													<td style={{ textAlign: "left" }}>{r.status}</td>
													<td style={{ textAlign: "left" }}>{r.remark || "—"}</td>
												</tr>
											))
										) : (
											<tr><td colSpan="7" className="empty-state">No attempted/incomplete payments found</td></tr>
										)}
									</tbody>
								</table>

								<h2>Failed Payments ({(dashboardData.failed || []).length})</h2>
								<table>
									<thead>
										<tr>
											<th style={{ textAlign: "left" }}>Date</th><th style={{ textAlign: "left" }}>Student</th><th style={{ textAlign: "left" }}>Origin</th><th style={{ textAlign: "left" }}>Method</th>
											<th className="num" style={{ textAlign: "left" }}>Attempted Amount</th><th style={{ textAlign: "left" }}>Status</th><th style={{ textAlign: "left" }}>Remark / Notes</th>
										</tr>
									</thead>
									<tbody>
										{(dashboardData.failed || []).length ? (
											(dashboardData.failed || []).map((r, i) => (
												<tr key={i}>
													<td style={{ textAlign: "left" }}>{r.date}</td>
													<td style={{ textAlign: "left" }}>{r.student}</td>
													<td style={{ textAlign: "left" }}>{r.origin}</td>
													<td style={{ textAlign: "left" }}>{r.method}</td>
													<td className="num" style={{ textAlign: "left" }}>{fmtMoney(r.amount)} {r.currency}</td>
													<td style={{ textAlign: "left" }}>{r.status}</td>
													<td style={{ textAlign: "left" }}>{r.remark || "—"}</td>
												</tr>
											))
										) : (
											<tr><td colSpan="7" className="empty-state">No failed payments found</td></tr>
										)}
									</tbody>
								</table>

								{renderFeeTable(`Transaction Fee Revenue by Payment Method (${(dashboardData.feeTables?.purchase || []).reduce((s, r) => s + r.count, 0)})`, (dashboardData.feeTables?.purchase || []))}
								{renderFeeTable(`Refund Fee Revenue by Payment Method (${(dashboardData.feeTables?.refund || []).reduce((s, r) => s + r.count, 0)})`, (dashboardData.feeTables?.refund || []))}
								{renderFeeTable(`Chargeback Fee Revenue by Payment Method (${(dashboardData.feeTables?.chargeback || []).reduce((s, r) => s + r.count, 0)})`, (dashboardData.feeTables?.chargeback || []))}
								{renderFeeTable(`Settlement Fee Revenue (${(dashboardData.feeTables?.settlement || []).reduce((s, r) => s + r.count, 0)})`, (dashboardData.feeTables?.settlement || []))}
								{(dashboardData.feeTables?.other || []).length > 0 &&
									renderFeeTable(`Other Fee Revenue by Payment Method (${(dashboardData.feeTables?.other || []).reduce((s, r) => s + r.count, 0)})`, (dashboardData.feeTables?.other || []))}

								<footer>
									Prepared directly from the Transaction Data for the filters applied above.
								</footer>
							</div>
						</Card>
					</>
				)}
			</Layout>
		</>
	);
};

export default SummaryReport;